/**
 * Import from uploaded dataset — the bridge into the revenue pages.
 *
 * Uploaded CSVs live in the datasets store, which Forecast and Compare read;
 * the revenue pages read from the revenue store. This dialog picks an uploaded
 * dataset, proposes a column mapping (from POST /api/revenue/import/suggest),
 * lets the user correct it with a sample-value preview per column, and runs
 * the import (POST /api/revenue/import/run) as either sales/transactions or
 * ad spend.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type DatasetSummary,
  type RevenueImportColumn,
  type RevenueImportMapping,
  type RevenueImportMode,
  type RevenueImportResult,
} from '../api';
import { Notice } from './Panels';

type MappingKey = keyof RevenueImportMapping;

interface FieldSpec {
  key: MappingKey;
  label: string;
  required: boolean;
}

const TRANSACTION_FIELDS: FieldSpec[] = [
  { key: 'date', label: 'Date', required: true },
  { key: 'amount', label: 'Amount', required: true },
  { key: 'customerEmail', label: 'Customer email', required: false },
  { key: 'customerName', label: 'Customer name', required: false },
  { key: 'channel', label: 'Channel', required: false },
  { key: 'campaign', label: 'Campaign', required: false },
];

const SPEND_FIELDS: FieldSpec[] = [
  { key: 'date', label: 'Date (period start)', required: true },
  { key: 'endDate', label: 'End date', required: false },
  { key: 'spend', label: 'Spend amount', required: true },
  { key: 'campaign', label: 'Campaign', required: true },
  { key: 'channel', label: 'Channel', required: false },
];

export function ImportRevenueDialog({
  initialMode = 'transactions',
  onClose,
  onImported,
}: {
  initialMode?: RevenueImportMode;
  onClose: () => void;
  onImported: () => void;
}) {
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [datasetId, setDatasetId] = useState('');
  const [mode, setMode] = useState<RevenueImportMode>(initialMode);
  const [columns, setColumns] = useState<RevenueImportColumn[]>([]);
  const [mapping, setMapping] = useState<RevenueImportMapping | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RevenueImportResult | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    api.listDatasets()
      .then((data) => setDatasets(data.datasets))
      .catch((error: unknown) => {
        setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
      });
  }, []);

  const loadSuggestion = useCallback(async (id: string) => {
    if (id === '') {
      setColumns([]);
      setMapping(null);
      return;
    }
    setSuggesting(true);
    setFailure(null);
    setResult(null);
    try {
      const suggestion = await api.revenueImportSuggest(id);
      setColumns(suggestion.columns);
      setMapping(suggestion.mapping);
    } catch (error) {
      setColumns([]);
      setMapping(null);
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setSuggesting(false);
    }
  }, []);

  useEffect(() => { void loadSuggestion(datasetId); }, [datasetId, loadSuggestion]);

  const fields = mode === 'transactions' ? TRANSACTION_FIELDS : SPEND_FIELDS;

  // In spend mode the amount column may have been suggested as `amount` rather
  // than `spend` — carry it over so the user is not asked to map it twice.
  const switchMode = (next: RevenueImportMode) => {
    setMode(next);
    setResult(null);
    if (next === 'spend') {
      setMapping((current) =>
        current !== null && current.spend === null && current.amount !== null
          ? { ...current, spend: current.amount }
          : current,
      );
    }
  };

  const missingRequired = mapping !== null && fields.some((f) => f.required && mapping[f.key] === null);

  const run = async () => {
    if (mapping === null || datasetId === '' || missingRequired) return;
    setRunning(true);
    setFailure(null);
    setResult(null);
    try {
      const outcome = await api.revenueImportRun({ datasetId, mode, mapping });
      setResult(outcome);
      if (outcome.imported > 0) onImported();
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Import from uploaded dataset"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <section className="card" style={{ maxWidth: 640, width: '100%', margin: 0 }}>
        <header>
          <h2>Import from uploaded dataset</h2>
          <button className="ghost tiny" onClick={onClose} aria-label="Close">✕</button>
        </header>

        {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

        {datasets.length === 0 && failure === null ? (
          <p className="muted">No uploaded datasets yet. Upload a CSV on the Upload page first, then come back.</p>
        ) : (
          <>
            <div className="controls" style={{ background: 'transparent', border: 'none', padding: 0 }}>
              <label className="field">
                <span>Dataset</span>
                <select value={datasetId} onChange={(e) => setDatasetId(e.target.value)}>
                  <option value="">Choose a dataset…</option>
                  {datasets.map((d) => (
                    <option key={d.id} value={d.id}>{d.name} ({d.rowCount.toLocaleString('en-US')} rows)</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="segmented" style={{ marginTop: 12 }}>
              <button aria-pressed={mode === 'transactions'} onClick={() => switchMode('transactions')}>
                Sales / transactions
              </button>
              <button aria-pressed={mode === 'spend'} onClick={() => switchMode('spend')}>
                Ad spend
              </button>
            </div>

            {suggesting && <p className="muted"><span className="spinner" /> Reading columns…</p>}

            {!suggesting && mapping !== null && (
              <>
                <p className="muted" style={{ marginTop: 12 }}>
                  Map each field to a column. The suggestion is a guess from column names — check it against the sample values.
                </p>
                <div className="controls" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                  {fields.map((field) => {
                    const picked = mapping[field.key];
                    const column = columns.find((c) => c.name === picked);
                    return (
                      <label className="field" key={field.key}>
                        <span>{field.label}{field.required ? ' *' : ''}</span>
                        <select
                          value={picked ?? ''}
                          onChange={(e) => setMapping({ ...mapping, [field.key]: e.target.value === '' ? null : e.target.value })}
                        >
                          <option value="">Not mapped</option>
                          {columns.map((c) => (
                            <option key={c.name} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                        {column !== undefined && column.sample.length > 0 && (
                          <span className="muted" style={{ fontSize: 12 }}>
                            e.g. {column.sample.join(', ')}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>

                {missingRequired && (
                  <p className="muted" role="alert">Map the required fields (marked *) before importing.</p>
                )}

                <div className="row" style={{ marginTop: 12 }}>
                  <button className="primary" onClick={() => void run()} disabled={running || missingRequired}>
                    {running ? <span className="spinner" /> : 'Import'}
                  </button>
                  <button className="ghost" onClick={onClose}>Cancel</button>
                </div>
              </>
            )}

            {result !== null && (
              <div className="card" style={{ marginTop: 12 }}>
                <p style={{ margin: 0 }}>
                  Imported {result.imported.toLocaleString('en-US')} row(s)
                  {result.skipped > 0 ? `, skipped ${result.skipped.toLocaleString('en-US')}` : ''}.
                  {result.imported > 0 ? ' Refresh the page to see the new figures.' : ''}
                </p>
                {result.errors.length > 0 && (
                  <ul className="muted" style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18 }}>
                    {result.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
